import React, { useState } from "react";
import {
  Button,
  LinearProgress,
  Typography,
  Box,
  Paper,
} from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import axios from "axios";
import { API_BASE_URL } from "../../utils/api";

const BulkInviteButton = ({ selectedUsers, onSuccess }) => {
  const [isSending, setIsSending] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentUser, setCurrentUser] = useState(null);

  const handleBulkInvite = async () => {
    if (!selectedUsers.length) return;

    const confirmed = window.confirm(
      `Send invitation emails to ${selectedUsers.length} user(s)?`
    );

    if (!confirmed) return;

    setIsSending(true);
    setProgress(0);
    setCurrentUser(null);

    try {
      const total = selectedUsers.length;
      let successCount = 0;
      let failureCount = 0;

      for (let i = 0; i < total; i++) {
        const username = selectedUsers[i];
        setCurrentUser(username);

        try {
          await axios.post(`${API_BASE_URL}/send-user-invitations`, {
            usernames: [username],
          });
          successCount++;
        } catch (err) {
          console.error(`Failed to invite ${username}`, err);
          failureCount++;
        }

        setProgress(Math.round(((i + 1) / total) * 100));
      }

      alert(
        `Invitations complete.\n✅ Sent: ${successCount}\n❌ Failed: ${failureCount}`
      );
      onSuccess?.();
    } catch (err) {
      console.error("Bulk invite error", err);
      alert("Failed to send invitations.");
    } finally {
      setIsSending(false);
      setCurrentUser(null);
      setProgress(0);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
      <Button
        variant="outlined"
        startIcon={<MailOutlineIcon />}
        disabled={selectedUsers.length === 0 || isSending}
        onClick={handleBulkInvite}
        sx={{
          borderRadius: "10px",
          textTransform: "none",
          fontWeight: 600,
          borderColor: "rgba(47,99,173,0.25)",
          color: "#2f63ad",
          px: 2.25,
          whiteSpace: "nowrap",
          "&:hover": {
            borderColor: "#2f63ad",
            backgroundColor: "rgba(47,99,173,0.04)",
          },
          "&.Mui-disabled": {
            borderColor: "rgba(0,0,0,0.12)",
          },
        }}
      >
        {isSending
          ? "Sending..."
          : `Send Invitations${selectedUsers.length ? ` (${selectedUsers.length})` : ""}`}
      </Button>

      {isSending && (
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            borderRadius: "12px",
            border: "1px solid rgba(47,99,173,0.08)",
            backgroundColor: "rgba(47,99,173,0.03)",
            minWidth: 260,
          }}
        >
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 8,
              borderRadius: 999,
              backgroundColor: "rgba(47,99,173,0.10)",
              "& .MuiLinearProgress-bar": {
                borderRadius: 999,
              },
            }}
          />

          <Typography
            variant="body2"
            sx={{
              mt: 1,
              color: "#223142",
              fontWeight: 600,
            }}
          >
            {progress}% complete
          </Typography>

          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: 0.35,
              color: "#6b778c",
              wordBreak: "break-word",
            }}
          >
            Sending invitation to {currentUser || "user"}...
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default BulkInviteButton;
